import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, describeAxiosError } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
      return data.user;
    } catch (_) {
      setUser(null);
      return null;
    }
  }, []);

  useEffect(() => {
    (async () => {
      // If we're mid-invite-claim, don't fire /auth/me — it will 401
      // and race with InviteClaim's claimInvite setUser call.
      if (typeof window !== "undefined" && window.location.pathname.startsWith("/invite/")) {
        setLoading(false);
        return;
      }
      await refresh();
      setLoading(false);
    })();
  }, [refresh]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (e) {
      // Log full error for debugging (users can copy this from console).
      // eslint-disable-next-line no-console
      console.error("[HorseTips4fun] login failed", {
        status: e.response?.status,
        detail: e.response?.data,
        code: e.code,
        message: e.message,
      });
      return { ok: false, error: describeAxiosError(e) };
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (_) {}
    setUser(null);
  };

  const claimInvite = async (token) => {
    try {
      const { data } = await api.post("/auth/claim-invite", { token });
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: describeAxiosError(e) };
    }
  };

  const updateProfile = async (name, avatar_emoji) => {
    try {
      const { data } = await api.post("/auth/profile", { name, avatar_emoji });
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: describeAxiosError(e) };
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, refresh, claimInvite, updateProfile }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
